import type { ImageProps } from '@mantine/core';
import { ImgHTMLAttributes } from 'react';

type ImageArgs = ImageProps & {
  src: string;
  width: number;
  height: number;
  layout?: 'fixed' | 'constrained';
  loader: (args: {
    src: string;
    width: number;
    height: number;
    quality: string | number;
  }) => string;
};

export default function getMantineCloudinaryImage({
  src,
  width,
  height,
  layout = 'fixed',
  loader,
  ...rest
}: ImageArgs): ImageProps & Omit<ImgHTMLAttributes<HTMLImageElement>, 'style'> {
  switch (layout) {
    case 'constrained': {
      const sizes = [
        160, 320, 640, 960, 1280, 1600, 1920, 2240, 2560, 2880, 3200,
      ].filter((size) => size < width);
      return {
        maw: width,
        height: 'auto',
        ...rest,
        src: loader({
          src,
          width: Math.round(width),
          height: Math.round(height),
          quality: 'auto',
        }),

        srcSet: [
          ...sizes.map(
            (size) =>
              `${loader({
                src,
                width: Math.round(size),
                height: Math.round((size / width) * height),
                quality: 'auto',
              })} ${size}w`
          ),
          `${loader({
            src,
            width: Math.round(width),
            height: Math.round(height),
            quality: 'auto',
          })}`,
        ].join(', '),
      };
    }
    case 'fixed':
    default: {
      return {
        width,
        height,
        ...rest,
        src: loader({
          src,
          width: Math.round(width),
          height: Math.round(height),
          quality: 'auto',
        }),
        srcSet: `${loader({
          src,
          width: Math.round(width * 2),
          height: Math.round(height * 2),
          quality: 'auto',
        })} 2x,
                   ${loader({
                     src,
                     width: Math.round(width * 3),
                     height: Math.round(height * 3),
                     quality: 'auto',
                   })} 3x`,
      };
    }
  }
}
